import React from 'react';

import { SelectTextField, type SingleSelectTextFieldProps } from '@lumapps/combobox/components/SelectTextField';
import { CommunitiesListRequestParams, Community } from '@lumapps/communities/types';
import { mdiCubeUnfolded } from '@lumapps/lumx/icons';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { useSpaceSelect } from '../../hooks/useSpaceSelect';

const SCOPE = 'space-select';

type ForwardedProps = Partial<
    Pick<
        SingleSelectTextFieldProps<Community>,
        | 'label'
        | 'placeholder'
        | 'helper'
        | 'icon'
        | 'isDisabled'
        | 'aria-disabled'
        | 'isRequired'
        | 'hasError'
        | 'error'
        | 'theme'
        | 'className'
        | 'id'
        | 'name'
        | 'hasClearButton'
        | 'readOnly'
        | 'scope'
        | 'inputRef'
        | 'onBlur'
        | 'onFocus'
        | 'listProps'
    >
>;

interface SpaceSelectBaseProps extends ForwardedProps {
    /**
     * Extra params forwarded to the spaces fetch
     * (e.g. `instanceId`, `privacy` for visibility filtering, `sortOrder`).
     */
    fetchParams?: Partial<CommunitiesListRequestParams>;
}

export interface SingleSpaceSelectProps extends SpaceSelectBaseProps {
    selectionType?: 'single';
    /** Selected space id. */
    value?: string;
    /** Callback with the selected space id (or `undefined` when cleared). */
    onChange?: (spaceId?: string) => void;
}

export interface MultipleSpaceSelectProps extends SpaceSelectBaseProps {
    selectionType: 'multiple';
    /** Selected space ids. */
    value?: string[];
    /** Callback with the selected space ids. */
    onChange?: (spaceIds: string[]) => void;
}

export type SpaceSelectProps = SingleSpaceSelectProps | MultipleSpaceSelectProps;

/**
 * Select field to pick one or several spaces.
 *
 * Spaces are fetched, searched and paginated under the hood. The component works with space
 * id(s): it accepts the selected id(s) as `value`, resolves them into space objects to display,
 * and returns the selected id(s) through `onChange`.
 *
 * @family Pickers
 */
export const SpaceSelect = ({
    selectionType = 'single',
    value,
    onChange,
    fetchParams,
    label,
    icon = mdiCubeUnfolded,
    scope = SCOPE,
    ...props
}: SpaceSelectProps) => {
    const { translateKey, translateObject } = useTranslate();

    const { spaces, selectedSpaces, status, isResolvingValue, onSearch, fetchMore, fetch } = useSpaceSelect({
        value,
        params: fetchParams,
    });

    // Merge resolved selected spaces into the options so the underlying lookups always succeed.
    const options = React.useMemo(() => {
        const merged = [...spaces];
        selectedSpaces.forEach((space) => {
            if (!merged.some((option) => option.id === space.id)) {
                merged.push(space);
            }
        });
        return merged;
    }, [spaces, selectedSpaces]);

    const handleChange = React.useCallback(
        (selection?: Community | Community[]) => {
            if (selectionType === 'multiple') {
                const selectedList = (selection as Community[] | undefined) ?? [];
                (onChange as MultipleSpaceSelectProps['onChange'])?.(selectedList.map((space) => space.id));
            } else {
                (onChange as SingleSpaceSelectProps['onChange'])?.((selection as Community | undefined)?.id);
            }
        },
        [onChange, selectionType],
    );

    const commonProps = {
        scope,
        label: label ?? translateKey(GLOBAL.SPACE),
        icon,
        autoFilter: false,
        options,
        getOptionId: 'id' as const,
        getOptionName: (space: Community) => translateObject(space.title) ?? '',
        // Surface a loading state while the selected id(s) are being resolved and there is
        // nothing to display yet, so a preset value doesn't show as an empty field.
        status: isResolvingValue && options.length === 0 ? BaseLoadingStatus.loading : status,
        onSearch,
        onLoadMore: fetchMore,
        onOpen: fetch,
        ...props,
    };

    return selectionType === 'multiple' ? (
        <SelectTextField<Community>
            {...commonProps}
            selectionType="multiple"
            value={selectedSpaces}
            onChange={handleChange}
        />
    ) : (
        <SelectTextField<Community>
            {...commonProps}
            selectionType="single"
            value={selectedSpaces[0]}
            onChange={handleChange}
        />
    );
};
